import { useState, useMemo } from 'react'
import MainTitle from '../../components/MainTitle'
import { useEffect } from 'react'
import useAxiosPrivate from '../../hooks/useAxiosPrivate'
import useAuth from '../../hooks/useAuth'
import moment from 'moment'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCalendarDays,
  faClock,
  faPhone,
  faUser,
  faChevronUp,
  faChevronDown,
  faArrowUpAZ,
  faArrowDownZA
} from '@fortawesome/free-solid-svg-icons'
import { Link, useNavigate } from 'react-router-dom'
import Datepicker from 'react-tailwindcss-datepicker'
import NotificationSlideOut from '../../components/NotificationSlideOut'

function Dashboard() {
  const { auth, setShowLoader } = useAuth()

  const [sortOrder, setSortOrder] = useState('asc')
  const [sortClientOrder, setSortClientOrder] = useState('asc')
  const [nextAppointment, setNextAppointment] = useState({})
  const [appointments, setAppointments] = useState([])
  const [updates, setUpdates] = useState([])
  const [open, setOpen] = useState(false);
  const [selectedThread, setSelectedThread] = useState(null);
  const [newMessages, setNewMessages] = useState([])
  const [yourClients, setYourClients] = useState([])
  const [value, setValue] = useState({
    startDate: moment(),
    endDate: moment().add(10, 'days'),
  })
  const [days, setDays] = useState({
    startDate: moment().subtract(7, 'days'),
    endDate: moment(),
  })
  const [updateDay, setUpdateDay] = useState({
    startDate: moment().subtract(7, 'days'),
    endDate: moment(),
  })
  const navigate = useNavigate()

  const axios = useAxiosPrivate()

  const sortedClients = useMemo(() => {
    const list = Array.isArray(yourClients) ? yourClients : [];
    return [...list].sort((a, b) => {
      if (a.name < b.name) return sortClientOrder === 'asc' ? -1 : 1
      if (a.name > b.name) return sortClientOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [yourClients, sortClientOrder])

  const getNextAppointment = async () => {
    setShowLoader(true)
    try {
      const response = await axios.get(
        `/users/${auth.user.id}/next-appointment`
      )
      if (!response.data.error) {
        setNextAppointment(response.data.result)
      }
    } catch (error) {}
    setShowLoader(false)
  }

  const getAppointments = async (start, end) => {
    try {
      const response = await axios.get(
        `/users/${auth.user.id}/sessions/${moment(start).format(
          'YYYY-MM-DD'
        )}/${moment(end).format('YYYY-MM-DD')}`
      )
      setAppointments(response.data.result)
    } catch (error) {}
  }

  const getUpdates = async (start, end) => {
    try {
      const response = await axios.get(
        `/notifications/user-updates/${auth.user.id}/${moment(start).format('YYYY-MM-DD')}/${moment(end).format('YYYY-MM-DD')}`
      )
      setUpdates(response.data.result)
    } catch (error) {}
  }

  const getNewMessages = async (start, end) => {
    setShowLoader(true)
    try {
      const response = await axios.get(
        `/users/${auth.user.id}/new-messages/${moment(start).format(
          'YYYY-MM-DD'
        )}/${moment(end).format('YYYY-MM-DD')}`
      )
      if (!response.data.error) {
        setNewMessages(response.data.result)
      }
    } catch (error) {}
    setShowLoader(false)
  }
  const sortMessages = () => {
    return [...newMessages].sort((a, b) => {
      if (sortOrder === 'asc') {
        return moment(a.created_at).valueOf() - moment(b.created_at).valueOf()
      } else {
        return moment(b.created_at).valueOf() - moment(a.created_at).valueOf()
      }
    })
  }

  const toggleSortOrder = () => {
    setSortOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'))
  }

  const getYourClients = async () => {
    setShowLoader(true)
    try {
      const response = await axios.get(`/clients/all/by-user/${auth.user.id}`)
      if (!response.data.error) {
        setYourClients(response.data.result)
      }
    } catch (error) {}
    setShowLoader(false)
  }

  const handleValueChange = (newValue) => {
    setValue(newValue)
    getAppointments(newValue.startDate, newValue.endDate)
  }

  const handleUpdateChange = (newValue) => {
    setUpdateDay(newValue)
    getUpdates(newValue.startDate, newValue.endDate)
  }

  const handleDayChange = (newValue) => {
    setDays(newValue)
    getNewMessages(newValue.startDate, newValue.endDate)
  }

  const viewMessage = (id, thread_reference, name) => {
    return navigate(`/clients/${id}/messages?thread=${thread_reference}`, {
      state: { name: name, id: id, reference: thread_reference },
    })
  }

  useEffect(() => {
    getYourClients()
    getNextAppointment()
    getNewMessages(days.startDate, days.endDate)
    getAppointments(value.startDate, value.endDate)
    getUpdates(updateDay.startDate, updateDay.endDate)

    const interval = setInterval(() => {
      getNextAppointment()
    }, 30000)

    return () => clearInterval(interval)
  }, [])

  const handleClick = (update) => {
    if (!update?.meta?.original_notification_id) {
      return
    }

    setOpen(true);
    setSelectedThread(update);
  }

  return (
    <section>
      <MainTitle title="Dashboard" />

      <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <article className="border-l-[5px] p-4 bg-white flex-1 border-l-wba-primary rounded-md shadow-md">
          <h3 className="text-base md:text-lg lg:text-xl font-medium mb-4 flex items-center">
            <p>Next Appointment</p>
          </h3>
          {nextAppointment && nextAppointment.id ? (
            <div className="border-t border-slate-200 py-3">
              <p className="mb-2 text-xs md:text-sm lg:text-base">
                <FontAwesomeIcon
                  icon={faCalendarDays}
                  className="text-slate-400 mr-2 text-sm lg:text-lg"
                />
                {moment(nextAppointment.start_time).format('dddd Do MMMM')}
              </p>
              <p className="mb-2 text-xs md:text-sm lg:text-base">
                <FontAwesomeIcon
                  icon={faClock}
                  className="text-slate-400 mr-2 text-sm lg:text-lg"
                />
                {moment(nextAppointment.start_time).format('H:mm')} -{' '}
                {moment(nextAppointment.end_time).format('H:mm')}
              </p>
              <p className="mb-2 text-xs md:text-sm lg:text-base">
                <FontAwesomeIcon
                  icon={faUser}
                  className="text-slate-400 mr-2 text-sm lg:text-lg"
                />
                {nextAppointment.client.name}
              </p>
              <p className="text-xs md:text-sm lg:text-base">
                <FontAwesomeIcon
                  icon={faPhone}
                  className="text-slate-400 mr-2 text-sm lg:text-lg"
                />
                {nextAppointment.service.title}
              </p>
              <Link
                to={`/clients/${nextAppointment.client.id}`}
                className="btn clear mt-4 text-xs md:text-sm lg:text-base"
              >
                View Client
              </Link>
            </div>
          ) : (
            <div>
              <p className="text-sm text-slate-500">
                No Appointments Scheduled
              </p>
            </div>
          )}
        </article>

        <article className="p-4 bg-white flex-1 rounded-md shadow-md">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-base md:text-lg lg:text-xl font-medium mb-4 flex items-center">
              <p>Your Clients</p>
            </h3>
            <button
              onClick={() => setSortClientOrder(sortClientOrder === 'asc' ? 'desc' : 'asc')}
              className="text-slate-500 text-sm flex items-center"
            >
              <FontAwesomeIcon
                icon={sortClientOrder === 'asc' ? faArrowUpAZ : faArrowDownZA}
                className="mr-2"
              />
              Sort {sortClientOrder === 'asc' ? 'A → Z' : 'Z → A'}
            </button>
          </div>

          {yourClients.length > 0 ? (
            <ul className="max-h-[250px] min-h-[136px] md:max-h-[250px] md:min-h-[136px] overflow-y-scroll">
              {sortedClients.map((client) => (
                <li key={client.id}>
                  <Link
                    to={`/clients/${client.id}`}
                    className="flex justify-between border-t border-slate-200 py-3 px-3 hover:bg-slate-100 text-xs md:text-sm lg:text-base"
                  >
                    <div>
                      <FontAwesomeIcon
                        icon={faUser}
                        className="text-slate-400 mr-4"
                      />
                      <span>{client.name}</span>
                    </div>
                    <p>{client.reference}</p>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">No Clients</p>
          )}
        </article>

        <article className="p-2 md:p-4 bg-white flex-1 rounded-md shadow-md">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <h3 className="text-base md:text-lg lg:text-xl font-medium mb-4 flex items-center">
              <p>Appointments</p>
            </h3>
            <div className="text-xs md:text-sm lg:text-base border border-gray-300 rounded-lg p-0.5">
              <Datepicker
                primaryColor={'blue'}
                value={value}
                onChange={handleValueChange}
                useRange={false}
              />
            </div>
          </div>
          {appointments?.length > 0 ? (
            <div>
              <ul className="max-h-[250px] min-h-[136px] overflow-y-scroll mt-4">
                {appointments?.map((appointment) => {
                  return (
                    <li key={appointment.id}>
                      <Link
                        to={`/clients/${appointment.client.id}`}
                        className="flex justify-between border-t border-slate-200 py-3 px-3 hover:bg-slate-100"
                      >
                        <p className="mb-2">
                          {moment(appointment.start_time).format(
                            'DD/MM/YY HH:mm'
                          )}
                        </p>
                        <p>{appointment.service.title}</p>
                        <div>
                          <span>{appointment.client.name}</span>
                        </div>
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </div>
          ) : (
            <div>
              <p className="text-sm text-slate-500">No Appointments</p>
            </div>
          )}
        </article>

        <article className="p-4 bg-white flex-1 rounded-md shadow-md">
          <div className="md:flex items-center justify-between">
            <h3 className="text-base md:text-lg lg:text-xl font-medium mb-4 flex items-center">
              <p>New Messages</p>
            </h3>
            <div className="flex items-center space-x-2">
              <div className="border border-gray-300 rounded-lg p-0.5 flex items-center">
                <Datepicker
                  primaryColor={'blue'}
                  value={days}
                  onChange={handleDayChange}
                  useRange={false}
                />
              </div>
              <button
                onClick={toggleSortOrder}
                className="ml-2 text-sm p-3 bg-blue-500 text-white rounded-md flex items-center"
              >
                Sort
                {sortOrder === 'asc' ? (
                  <FontAwesomeIcon icon={faChevronUp} className="ml-2" />
                ) : (
                  <FontAwesomeIcon icon={faChevronDown} className="ml-2" />
                )}
              </button>
            </div>
          </div>
          {newMessages.length > 0 ? (
            <div>
              <ul className="max-h-[250px] min-h-[136px] overflow-y-scroll mt-4">
                {sortMessages()?.map((message) => {
                  return (
                    <li
                      key={message.id}
                      onClick={() =>
                        viewMessage(
                          message.client_id,
                          message.reference,
                          message.name,
                          message.client_id
                        )
                      }
                      className="hover:bg-slate-100 cursor-pointer"
                    >
                      <div className="border-b py-2">
                        <div className="flex items-center justify-between">
                          <div>{message.name}</div>
                          <div>
                            {moment(message.created_at).format('DD/MM/YYYY')}
                          </div>
                        </div>
                        <p className="text-xs">{message.body}</p>
                      </div>
                    </li>
                  )
                })}
              </ul>
            </div>
          ) : (
            <div>
              <p className="text-sm text-slate-500">No New Messages</p>
            </div>
          )}
        </article>
        <article className="p-4 bg-white flex-1 rounded-md shadow-md">
          <div className="md:flex items-center justify-between">
            <h3 className="text-base md:text-lg lg:text-xl font-medium mb-4 flex items-center">
              <p>Updates</p>
            </h3>
            <div className="text-xs md:text-sm lg:text-base border border-gray-300 rounded-lg p-0.5">
              <Datepicker
                primaryColor={'blue'}
                value={updateDay}
                onChange={handleUpdateChange}
                useRange={false}
              />
            </div>
          </div>
          {updates.length > 0 ? (
            <div>
              <ul className="max-h-[250px] min-h-[136px] overflow-y-scroll mt-4">
                {updates?.map((update) => {
                  return (
                    <li
                      onClick={() => handleClick(update)}
                      key={update.id}
                      className="hover:bg-slate-100 cursor-pointer"
                    >
                      <div className="border-b py-2">
                        <div className="flex items-center justify-between">
                          <div>{update.title}</div>
                          <div>
                            {moment(update.created_at).format('DD/MM/YYYY')}
                          </div>
                        </div>
                        <p className="text-xs">{update.body}</p>
                      </div>
                    </li>
                  )
                })}
              </ul>
            </div>
          ) : (
            <div>
              <p className="text-sm text-slate-500">
                No updates in the last 7 days
              </p>
            </div>
          )}
        </article>
      </section>

      <NotificationSlideOut open={open} setOpen={setOpen} message={selectedThread} />
    </section>
  )
}

export default Dashboard
